/** @format */

import dayjs from 'dayjs'

/** 自定义时间范围标识 */
export const CUSTOM_RANGE_KEY = 'custom'

export interface QuickRangeItem {
  label: string
  value: string
}

type QuickRangePreset = {
  label: string
  getRange: () => [number, number]
}

/** 全部内置快捷选项（可通过 quickRange 按需选用） */
export const QUICK_RANGE_PRESETS: Record<string, QuickRangePreset> = {
  '5min': {
    label: '近5分钟',
    getRange: () => {
      const end = dayjs()
      return [end.subtract(5, 'minute').unix(), end.unix()]
    }
  },
  '15min': {
    label: '近15分钟',
    getRange: () => {
      const end = dayjs()
      return [end.subtract(15, 'minute').unix(), end.unix()]
    }
  },
  '0.5h': {
    label: '近0.5小时',
    getRange: () => {
      const end = dayjs()
      return [end.subtract(30, 'minute').unix(), end.unix()]
    }
  },
  '1h': {
    label: '近1小时',
    getRange: () => {
      const end = dayjs()
      return [end.subtract(1, 'hour').unix(), end.unix()]
    }
  },
  '2h': {
    label: '近2小时',
    getRange: () => {
      const end = dayjs()
      return [end.subtract(2, 'hour').unix(), end.unix()]
    }
  },
  '3h': {
    label: '近3小时',
    getRange: () => {
      const end = dayjs()
      return [end.subtract(3, 'hour').unix(), end.unix()]
    }
  },
  '6h': {
    label: '近6小时',
    getRange: () => {
      const end = dayjs()
      return [end.subtract(6, 'hour').unix(), end.unix()]
    }
  },
  '12h': {
    label: '近12小时',
    getRange: () => {
      const end = dayjs()
      return [end.subtract(12, 'hour').unix(), end.unix()]
    }
  },
  '1day': {
    label: '近1天',
    getRange: () => {
      const end = dayjs()
      return [end.subtract(1, 'day').unix(), end.unix()]
    }
  },
  '3day': {
    label: '近3天',
    getRange: () => {
      const end = dayjs()
      return [end.subtract(3, 'day').unix(), end.unix()]
    }
  },
  '1week': {
    label: '近1周',
    getRange: () => {
      const end = dayjs()
      return [end.subtract(7, 'day').unix(), end.unix()]
    }
  },
  '30day': {
    label: '近30天',
    getRange: () => {
      const end = dayjs()
      return [end.subtract(30, 'day').unix(), end.unix()]
    }
  },
  today: {
    label: '今天',
    getRange: () => {
      const end = dayjs()
      return [end.startOf('day').unix(), end.unix()]
    }
  },
  yesterday: {
    label: '昨天',
    getRange: () => {
      const day = dayjs().subtract(1, 'day')
      return [day.startOf('day').unix(), day.endOf('day').unix()]
    }
  },
  thisWeek: {
    label: '本周',
    getRange: () => {
      const end = dayjs()
      return [end.startOf('week').unix(), end.unix()]
    }
  },
  lastWeek: {
    label: '上周',
    getRange: () => {
      const week = dayjs().subtract(1, 'week')
      return [week.startOf('week').unix(), week.endOf('week').unix()]
    }
  },
  thisMonth: {
    label: '本月',
    getRange: () => {
      const end = dayjs()
      return [end.startOf('month').unix(), end.unix()]
    }
  },
  lastMonth: {
    label: '上月',
    getRange: () => {
      const month = dayjs().subtract(1, 'month')
      return [month.startOf('month').unix(), month.endOf('month').unix()]
    }
  }
}

/** 默认展示的快捷按钮 */
export const DEFAULT_QUICK_RANGE: QuickRangeItem[] = [
  { label: QUICK_RANGE_PRESETS['1h'].label, value: '1h' },
  { label: QUICK_RANGE_PRESETS['1day'].label, value: '1day' },
  { label: QUICK_RANGE_PRESETS['3day'].label, value: '3day' },
  { label: QUICK_RANGE_PRESETS['1week'].label, value: '1week' },
  { label: '自定义', value: CUSTOM_RANGE_KEY }
]

/** 全部内置快捷选项列表（不含自定义） */
export const ALL_QUICK_RANGE_ITEMS: QuickRangeItem[] = Object.entries(QUICK_RANGE_PRESETS).map(
  ([value, preset]) => ({ label: preset.label, value })
)

/** 根据 preset key 列表生成 quickRange 配置 */
export const buildQuickRange = (keys: string[], includeCustom = true): QuickRangeItem[] => {
  const items = keys
    .filter(key => key !== CUSTOM_RANGE_KEY)
    .map(key => {
      const preset = QUICK_RANGE_PRESETS[key]
      return preset ? { label: preset.label, value: key } : { label: key, value: key }
    })
  if (includeCustom) {
    items.push({ label: '自定义', value: CUSTOM_RANGE_KEY })
  }
  return items
}

export const isKnownQuickRangeKey = (key: string): boolean =>
  key === CUSTOM_RANGE_KEY || key in QUICK_RANGE_PRESETS

/** 根据快捷选项 key 计算时间范围（秒级时间戳） */
export const getQuickRangeDateValue = (key: string): [number, number] | null => {
  if (key === CUSTOM_RANGE_KEY) {
    return null
  }
  const preset = QUICK_RANGE_PRESETS[key]
  return preset ? preset.getRange() : null
}

/** 秒级时间戳转毫秒（供 Datepicker 使用） */
export const secToMs = (time: [number, number]): [number, number] => [
  time[0] * 1000,
  time[1] * 1000
]

/** 毫秒时间戳转秒 */
export const msToSec = (time: [number, number]): [number, number] => [
  Math.floor(time[0] / 1000),
  Math.floor(time[1] / 1000)
]

const RANGE_MATCH_END_TOLERANCE_MIN = 1

/** 秒级时间戳截断到分钟（用于快捷选项反推，避免秒级误差） */
const truncateToMinute = (ts: number): number => dayjs.unix(ts).startOf('minute').unix()

/** 判断两个秒级时间范围是否匹配同一快捷选项（精确到分钟） */
const isSameQuickRange = (actual: [number, number], expected: [number, number]): boolean => {
  return (
    truncateToMinute(actual[0]) === truncateToMinute(expected[0]) &&
    truncateToMinute(actual[1]) === truncateToMinute(expected[1])
  )
}

/** 根据时间范围（秒）反推快捷选项 key，无法匹配时返回 custom */
export const getQuickKeyFromRange = (date: [number, number]): string => {
  const [, end] = date
  const endMinuteDiff = truncateToMinute(dayjs().unix()) - truncateToMinute(end)
  if (endMinuteDiff > RANGE_MATCH_END_TOLERANCE_MIN * 60) {
    return CUSTOM_RANGE_KEY
  }
  for (const key of Object.keys(QUICK_RANGE_PRESETS)) {
    const expected = getQuickRangeDateValue(key)
    if (expected && isSameQuickRange(date, expected)) {
      return key
    }
  }
  return CUSTOM_RANGE_KEY
}

/** 判断秒级时间范围是否在最大天数限制内 */
export const isRangeWithinMaxDays = (range: [number, number], maxDays: number): boolean => {
  const [start, end] = range
  if (!Number.isFinite(start) || !Number.isFinite(end) || start > end) {
    return false
  }
  return end - start <= maxDays * 86400
}

/** value / defaultQuickKey 支持的入参类型；可为空表示不选中 */
export type CustomDatePickerValue = [number, number] | string | null | undefined

/** 判断入参是否为空（未传 / null / 空字符串） */
export const isEmptyCustomDatePickerValue = (
  input: CustomDatePickerValue
): input is null | undefined | '' => input == null || input === ''

export const isQuickRangeKey = (
  key: string,
  quickRange: QuickRangeItem[] = DEFAULT_QUICK_RANGE
): boolean => quickRange.some(item => item.value === key) || isKnownQuickRangeKey(key)

/** 将 value 或 defaultQuickKey 解析为快捷 key 与秒级时间范围；空入参返回 null */
export const resolveCustomDatePickerValue = (
  input: CustomDatePickerValue,
  quickRange: QuickRangeItem[] = DEFAULT_QUICK_RANGE
): { quickKey: string; range?: [number, number] } | null => {
  if (isEmptyCustomDatePickerValue(input)) {
    return null
  }
  if (typeof input === 'string') {
    if (!isKnownQuickRangeKey(input) && !quickRange.some(item => item.value === input)) {
      return null
    }
    if (input === CUSTOM_RANGE_KEY) {
      return { quickKey: CUSTOM_RANGE_KEY }
    }
    const range = getQuickRangeDateValue(input)
    return range ? { quickKey: input, range } : { quickKey: input }
  }
  return { quickKey: getQuickKeyFromRange(input), range: input }
}
